import React from 'react';
import { ArrowLeft, Smartphone, ShieldCheck, FileText, Send, CheckCircle, History, Globe, Building2, Stamp } from 'lucide-react';
import { PhoneMockup } from '../components/PhoneMockup';
import { Screen1 } from '../components/screens/Screen1';
import { Screen2 } from '../components/screens/Screen2';
import { Screen3 } from '../components/screens/Screen3';
import { Screen4 } from '../components/screens/Screen4';
import { Screen5 } from '../components/screens/Screen5';
import { Screen6 } from '../components/screens/Screen6';
import { Screen7 } from '../components/screens/Screen7';
import { Screen8 } from '../components/screens/Screen8';
import { Screen9 } from '../components/screens/Screen9';
interface DemoViewProps {
  currentScreen: number;
  onNavigate: (screenId: number) => void;
  onBack: () => void;
}
const NAVIGATION_GROUPS = [{
  title: 'User Flow',
  items: [{
    id: 1,
    label: 'Secure Access',
    icon: <Smartphone className="w-4 h-4" data-id="element-439" />
  }, {
    id: 2,
    label: 'Home Dashboard',
    icon: <FileText className="w-4 h-4" data-id="element-440" />
  }, {
    id: 3,
    label: 'Document Detail',
    icon: <ShieldCheck className="w-4 h-4" data-id="element-441" />
  }]
}, {
  title: 'Verification Flow',
  items: [{
    id: 4,
    label: 'Submit to Receiver',
    icon: <Send className="w-4 h-4" data-id="element-442" />
  }, {
    id: 5,
    label: 'Verification Result',
    icon: <CheckCircle className="w-4 h-4" data-id="element-443" />
  }]
}, {
  title: 'Tracking & Compliance',
  items: [{
    id: 6,
    label: 'Activity & Audit Trail',
    icon: <History className="w-4 h-4" data-id="element-444" />
  }]
}, {
  title: 'Network & Institutions',
  items: [{
    id: 7,
    label: 'Global Trust Network',
    icon: <Globe className="w-4 h-4" data-id="element-445" />
  }, {
    id: 8,
    label: 'Institution Mode',
    icon: <Building2 className="w-4 h-4" data-id="element-446" />
  }]
}, {
  title: 'Digital Apostille',
  items: [{
    id: 9,
    label: 'Signature Screen',
    icon: <Stamp className="w-4 h-4" data-id="element-447" />
  }]
}];
export function DemoView({
  currentScreen,
  onNavigate,
  onBack
}: DemoViewProps) {
  const renderScreen = () => {
    switch (currentScreen) {
      case 1:
        return <Screen1 onNavigate={onNavigate} data-id="element-448" />;
      case 2:
        return <Screen2 onNavigate={onNavigate} data-id="element-449" />;
      case 3:
        return <Screen3 onNavigate={onNavigate} data-id="element-450" />;
      case 4:
        return <Screen4 onNavigate={onNavigate} data-id="element-451" />;
      case 5:
        return <Screen5 onNavigate={onNavigate} data-id="element-452" />;
      case 6:
        return <Screen6 onNavigate={onNavigate} data-id="element-453" />;
      case 7:
        return <Screen7 onNavigate={onNavigate} data-id="element-454" />;
      case 8:
        return <Screen8 onNavigate={onNavigate} data-id="element-455" />;
      case 9:
        return <Screen9 onNavigate={onNavigate} data-id="element-456" />;
      default:
        return <Screen1 onNavigate={onNavigate} data-id="element-457" />;
    }
  };
  return <div className="flex h-screen bg-slate-100 overflow-hidden" data-id="element-458">
      {/* Sidebar Navigation */}
      <div className="w-80 bg-white border-r border-slate-200 flex flex-col h-full z-10 shadow-sm" data-id="element-459">
        <div className="p-6 border-b border-slate-100" data-id="element-460">
          <button onClick={onBack} className="flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-datmont-900 transition-colors mb-6" data-id="element-461">
            <ArrowLeft className="w-4 h-4" data-id="element-462" /> Back to Overview
          </button>
          <div className="flex items-center gap-3" data-id="element-463">
            <img src="https://cdn.magicpatterns.com/uploads/dN76LW7ZJU33sweBnWUYDh/Datmont_with_Apostel_Seal_copy.png" alt="Datmont Logo" className="h-8 object-contain" data-id="element-464" />
            <span className="text-lg font-bold text-datmont-900 tracking-tight" data-id="element-465">
              Interactive Demo
            </span>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto custom-scrollbar p-4 space-y-6" data-id="element-466">
          {NAVIGATION_GROUPS.map((group, idx) => <div key={idx} data-id="element-467">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 px-3" data-id="element-468">
                {group.title}
              </h3>
              <div className="space-y-1" data-id="element-469">
                {group.items.map(item => <button key={item.id} onClick={() => onNavigate(item.id)} className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${currentScreen === item.id ? 'bg-blue-50 text-datmont-blue' : 'text-slate-600 hover:bg-slate-50 hover:text-datmont-900'}`} data-id="element-470">
                    <div className={`${currentScreen === item.id ? 'text-datmont-blue' : 'text-slate-400'}`} data-id="element-471">
                      {item.icon}
                    </div>
                    {item.label}
                    {currentScreen === item.id && <div className="ml-auto w-1.5 h-1.5 rounded-full bg-datmont-blue" data-id="element-472"></div>}
                  </button>)}
              </div>
            </div>)}
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex items-center justify-center p-8 bg-[radial-gradient(#e2e8f0_1px,transparent_1px)] [background-size:16px_16px]" data-id="element-473">
        <div className="relative" data-id="element-474">
          {/* Decorative elements behind phone */}
          <div className="absolute -inset-10 bg-gradient-to-tr from-blue-100/50 to-indigo-100/50 blur-3xl rounded-full -z-10" data-id="element-475"></div>

          <PhoneMockup data-id="element-476">{renderScreen()}</PhoneMockup>
        </div>
      </div>
    </div>;
}